import { Plus, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MentionInput } from '@/components/mentions/MentionInput';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { usePipelineContext } from '../../context';
import { getUserInitials, formatNoteTimestamp, formatNotesWithAttribution } from '../../utils/formatUtils';
import type { Customer } from '../../types';

interface PipelineCardNotesProps {
  customer: Customer;
}

/**
 * Notes section for expanded pipeline cards
 * Allows adding new notes with @mentions and displays existing notes
 */
export function PipelineCardNotes({ customer }: PipelineCardNotesProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { state, actions } = usePipelineContext();
  const localNotes = state.localNotes[customer.id] ?? '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const newNoteContent = localNotes.trim();

    if (!newNoteContent) {
      toast({
        title: 'Empty note',
        description: 'Please enter a note before saving.',
        variant: 'destructive',
      });
      return;
    }

    // Build attribution with user initials and timestamp
    const initials = user
      ? getUserInitials(
          user.firstName,
          user.lastName,
          user.full_name || user.displayName,
          user.email
        )
      : 'U';
    const timestamp = formatNoteTimestamp();
    const attributedNote = `[${initials} - ${timestamp}] ${newNoteContent}`;

    // Prepend new note to existing notes
    const existingNotes = customer.pipelineNotes ?? '';
    const notesToSave = existingNotes.trim()
      ? `${attributedNote}\n\n${existingNotes}`
      : attributedNote;

    // Save notes to the server
    apiRequest<{ success: boolean }>('POST', `/api/customers/${customer.id}/notes`, { notes: notesToSave })
      .then((data) => {
        if (data.success) {
          toast({
            title: 'Note added',
            description: 'Your note has been saved.',
          });

          // Clear the input
          actions.setLocalNotes(customer.id, '');

          // Refresh data
          queryClient.invalidateQueries({
            queryKey: ['/api/pipeline-stages/data'],
          });
        } else {
          toast({
            title: 'Error',
            description: 'Failed to save note. Please try again.',
            variant: 'destructive',
          });
        }
      })
      .catch((error) => {
        console.error('Error saving notes:', error);
        toast({
          title: 'Error',
          description: 'Failed to save note. Please try again.',
          variant: 'destructive',
        });
      });
  };

  return (
    <div
      className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-700/60"
      onClick={(e) => {
        // Prevent stage collapse when clicking anywhere in notes section
        e.stopPropagation();
      }}
    >
      {/* Add New Note Section */}
      <div className="space-y-2">
        <div className="text-xs font-medium flex items-center">
          <Plus className="h-3.5 w-3.5 mr-1.5 text-primary" />
          Add Note (use @ to mention team)
        </div>
        <form
          className="flex flex-col space-y-2"
          data-customer-id={customer.id}
          onSubmit={handleSubmit}
        >
          <MentionInput
            value={localNotes}
            onChange={(value) => {
              actions.setLocalNotes(customer.id, value);
            }}
            placeholder="Type a new note... (Cmd/Ctrl+Enter to save)"
            multiline
            className="text-xs"
            onMention={() => {
              // Mention tracking can be added here if needed
            }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            onFocus={(e: React.FocusEvent) => {
              e.stopPropagation();
            }}
            onSubmitShortcut={() => {
              const form = document.querySelector(
                `form[data-customer-id="${customer.id}"]`
              ) as HTMLFormElement;
              if (form) {
                form.requestSubmit();
              }
            }}
          />
          <Button
            type="submit"
            variant="outline"
            size="sm"
            className="text-xs self-end"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Note
          </Button>
        </form>
      </div>

      {/* Existing Notes Display */}
      {customer.pipelineNotes && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium flex items-center text-slate-500 dark:text-slate-400">
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Note History
          </div>
          <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-200 dark:border-slate-700 max-h-32 overflow-y-auto">
            <div className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
              {formatNotesWithAttribution(customer.pipelineNotes)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
